/**
 * What a section card / status dot shows: the server's status, unless something on this
 * phone has not reached the server yet (pending, failed or conflicted outbox item).
 */
import type { SectionName, SectionStatus } from "@npac/rules";
import type { Booth, OutboxItem, SectionOutboxItem, User } from "./types";

export type UiStatus = "complete" | "partial" | "mismatch" | "not_started" | "waiting" | "conflict" | "failed";

export interface SectionView {
  status: UiStatus;
  /** status of the local (unsent) copy, computed with @npac/rules checkSection */
  localStatus: SectionStatus | null;
  /** status the server last reported */
  serverStatus: SectionStatus | null;
  hasDraft: boolean;
  item: SectionOutboxItem | null;
}

export function sectionView(
  booth: Pick<Booth, "code" | "sections"> | null,
  section: SectionName,
  outbox: readonly OutboxItem[],
  draftSections: readonly SectionName[] = []
): SectionView {
  const serverStatus = booth?.sections?.[section]?.status ?? null;
  const item =
    (outbox.find((o) => o.kind === "section" && o.section === section && (!booth || o.boothCode === booth.code)) as SectionOutboxItem | undefined) ?? null;
  const hasDraft = draftSections.includes(section);
  if (item) {
    const status: UiStatus = item.status === "conflict" ? "conflict" : item.status === "failed" ? "failed" : "waiting";
    return { status, localStatus: item.payload.status, serverStatus, hasDraft, item };
  }
  return { status: serverStatus ?? "not_started", localStatus: null, serverStatus, hasDraft, item: null };
}

/**
 * Surveyors with allowedBooths may edit only those booth numbers (45 also covers 45A, as on the API).
 * Polling-day workers likewise act only for the booths whose list has their mobile.
 */
export function canEditBooth(user: Pick<User, "role" | "allowedBooths"> | null, booth: Pick<Booth, "no">): boolean {
  if (!user) return false;
  // a polling-day link without a booth covers every booth of its assembly (allowedBooths null)
  if (user.role === "worker") return user.allowedBooths == null || user.allowedBooths.includes(booth.no);
  if (user.role !== "surveyor") return true;
  return user.allowedBooths == null || user.allowedBooths.includes(booth.no);
}

export type LocationUiStatus = "shared" | "waiting" | "failed" | "none";

export function locationView(booth: Pick<Booth, "code" | "location"> | null, outbox: readonly OutboxItem[]): LocationUiStatus {
  const items = outbox.filter((o) => o.kind === "location" && (!booth || o.boothCode === booth.code));
  if (items.some((i) => i.status === "pending")) return "waiting";
  if (items.some((i) => i.status === "failed")) return "failed";
  return booth?.location ? "shared" : "none";
}
